import React, { useContext, useEffect, useState } from 'react';
import { inferTipoColuna } from '../../lib/inferTipo';
import { useSearchParams } from 'react-router-dom';
import { GlobalContext } from '../../lib/GlobalVar';
import {
    ConsultaConexoes, ConsultaConstrutor, PublicaTela, RemoveInterna,
    ConsultaLogins, ExecutaTelaInterna, ConsultaPermissoes
} from '../../lib/api';
import Swal from 'sweetalert2';
import Icon from '../Icon';
import { CHART_TIPOS, camposDoTipo, labelCampo } from '../../lib/chartTypes';

// Insere um token no textarea pelo id, na posicao do cursor (fallback: fim). Atualiza via setter.
function insereTokenTextarea(id, valorAtual, token, setter) {
    const el = (typeof document !== 'undefined') ? document.getElementById(id) : null;
    const v = valorAtual || '';
    let ini = v.length, fim = v.length;
    if (el && el.selectionStart != null) { ini = el.selectionStart; fim = el.selectionEnd; }
    const novo = v.slice(0, ini) + token + v.slice(fim);
    setter(novo);
    if (el) setTimeout(() => { try { el.focus(); el.selectionStart = el.selectionEnd = ini + token.length; } catch (_) {} }, 0);
}

const PASSOS = [
    { id: 'id',     label: 'Identidade' },
    { id: 'origem', label: 'Origem' },
    { id: 'cols',   label: 'Colunas' },
    { id: 'dash',   label: 'Dashboard' }
];

const VAZIO = {
    id: null, nome: '', slug: '', descricao: '', mensagem_padrao: '',
    origem: 'banco',  // 'banco' | 'rest'
    conexao: '',      // se banco
    sql: 'SELECT id, nome FROM minha_tabela LIMIT 50',
    rest: { url: '', method: 'GET', headers: [], auth: { tipo: 'none', token: '', user: '', senha: '' }, path_rows: '', shape: 'auto' },
    colunas: [],      // [{ nome, label, visivel, tipo }]
    args: [],
    modo_acesso: 'todos', // 'todos' | 'setor' | 'usuarios'
    setor: '',
    setores: [],      // multi-setor (substitui o setor unico legado)
    usuarios: [],     // ids de usuarios
    charts: []        // [{ tipo: 'bar'|'line'|'pie', titulo, x, y }]
};

const SETORES_CONS = ['TI', 'Comercial', 'Marketing', 'Financeiro', 'Operacoes', 'Logistica', 'Relacionamento', 'Diretoria', 'APP'];


// Normaliza tela vinda do construtor.json (formatos antigos misturam objeto/string em origem)
function normalizaTela(t) {
    if (!t || typeof t !== 'object') return { ...VAZIO };
    const out = { ...VAZIO, ...t };
    out.setores = (Array.isArray(t.setores) && t.setores.length) ? t.setores : (t.setor ? [t.setor] : []);
    if (t.origem && typeof t.origem === 'object') {
        // Legado: origem = { tipo, metodo, endpoint }
        const tipo = t.origem.tipo === 'rest' ? 'rest' : 'banco';
        out.origem = tipo;
        if (tipo === 'rest') {
            out.rest = {
                ...VAZIO.rest,
                ...(t.rest || {}),
                method: t.origem.metodo || t.rest?.method || 'GET',
                url: t.origem.endpoint || t.rest?.url || '',
            };
        }
    } else {
        out.origem = (t.origem === 'rest') ? 'rest' : 'banco';
        out.rest = { ...VAZIO.rest, ...(t.rest || {}) };
    }
    return out;
}

const labelOrigem = (t) => {
    if (!t) return 'banco';
    if (typeof t.origem === 'string') return t.origem;
    if (t.origem && typeof t.origem === 'object') return t.origem.tipo || 'rest';
    return 'banco';
};


export default function TIConstrutor() {
    const { user } = useContext(GlobalContext);
    const [params] = useSearchParams();
    const editarSlug = params.get('editar');
    const [telas, setTelas] = useState([]);
    const [conexoes, setConexoes] = useState([]);
    const [usuariosAll, setUsuariosAll] = useState([]);
    const [passo, setPasso] = useState(0);
    const [form, setForm] = useState(VAZIO);
    const [popupUsers, setPopupUsers] = useState(false);
    const [previewRows, setPreviewRows] = useState([]);
    const [previewCols, setPreviewCols] = useState([]);
    const [testing, setTesting] = useState(false);
    const [setoresDisp, setSetoresDisp] = useState(SETORES_CONS);

    useEffect(() => {
        (async () => {
            const [tt, cc, uu, pp] = await Promise.all([
                ConsultaConstrutor({}),
                ConsultaConexoes({}),
                ConsultaLogins({}),
                ConsultaPermissoes({})
            ]);
            const lista = Array.isArray(tt) ? tt : [];
            setTelas(lista);
            setConexoes(Array.isArray(cc) ? cc : (cc.conexoes || []));
            setUsuariosAll(Array.isArray(uu) ? uu : (uu.users || []));
            if (pp && !pp.Error && Array.isArray(pp.setores) && pp.setores.length) setSetoresDisp(pp.setores);
            if (editarSlug) {
                const t = lista.find(x => x.slug === editarSlug);
                if (t) setForm(normalizaTela(t));
            }
        })();
    }, [editarSlug]);

    function up(k, v) { setForm(f => ({ ...f, [k]: v })); }
    function upRest(k, v) { setForm(f => ({ ...f, rest: { ...f.rest, [k]: v } })); }
    function upAuth(k, v) { setForm(f => ({ ...f, rest: { ...f.rest, auth: { ...f.rest.auth, [k]: v } } })); }

    function addHeader() { upRest('headers', [...(form.rest.headers || []), { nome: '', valor: '' }]); }
    function delHeader(i) { upRest('headers', form.rest.headers.filter((_, ix) => ix !== i)); }
    function updHeader(i, k, v) {
        const arr = [...form.rest.headers];
        arr[i] = { ...arr[i], [k]: v };
        upRest('headers', arr);
    }

    function addColuna() { up('colunas', [...(form.colunas || []), { nome: '', label: '', visivel: true, tipo: 'texto' }]); }
    function delColuna(i) { up('colunas', form.colunas.filter((_, ix) => ix !== i)); }
    function updColuna(i, k, v) {
        const arr = [...form.colunas];
        arr[i] = { ...arr[i], [k]: v };
        up('colunas', arr);
    }

    function addChart() { up('charts', [...(form.charts || []), { tipo: 'bar', titulo: 'Novo gráfico', x: '', y: '' }]); }
    function delChart(i) { up('charts', form.charts.filter((_, ix) => ix !== i)); }
    function updChart(i, k, v) {
        const arr = [...form.charts];
        arr[i] = { ...arr[i], [k]: v };
        up('charts', arr);
    }

    async function testarOrigem() {
        if (!form.slug) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe o slug primeiro pra testar.' });
        setTesting(true);
        // salva temporariamente pra usar o runtime
        await PublicaTela({ solicitante: user?.email, tela: { ...form, publicada: false } });
        const r = await ExecutaTelaInterna({ slug: form.slug, args: {} });
        setTesting(false);
        if (r.Error) {
            // mensagem rica do backend (SQL syntax, conn refused, HTTP code+body, etc.)
            return Swal.fire({
                icon: 'error',
                title: 'Erro ao testar origem',
                html: `<div style="text-align:left;font-family:monospace;font-size:12.5px;white-space:pre-wrap;word-break:break-word;">${
                    String(r.Error).replace(/[&<>]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]))
                }</div>`,
                width: 640,
                confirmButtonText: 'OK'
            });
        }
        setPreviewCols(r.colunas || []);
        setPreviewRows(r.rows || []);
        // auto-popula colunas se vazias
        if ((form.colunas || []).length === 0 && r.colunas) {
            up('colunas', r.colunas.map(c => ({ nome: c.nome, label: c.label || c.nome, visivel: true, tipo: (r.rows ? inferTipoColuna(r.rows, c.nome) : 'texto') })));
        }
        Swal.fire({ icon: 'success', title: 'Origem OK', text: `${(r.rows || []).length} linhas retornadas.`, timer: 1400, showConfirmButton: false });
    }

    async function publicar() {
        if (!form.nome || !form.slug) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Nome e slug são obrigatórios.' });
        if (form.origem === 'banco' && (!form.conexao || !form.sql)) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Conexão e SQL obrigatórios para origem banco.' });
        if (form.origem === 'rest' && !form.rest.url) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'URL obrigatória para origem REST.' });
        const resp = await PublicaTela({ solicitante: user?.email, tela: { ...form, publicada: true } });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        Swal.fire({ icon: 'success', title: 'Tela publicada', text: `/internas/${form.slug}`, timer: 1500, showConfirmButton: false });
        const ttl = await ConsultaConstrutor({});
        setTelas(Array.isArray(ttl) ? ttl : []);
    }

    async function excluirTela(t) {
        const c = await Swal.fire({
            icon: 'warning',
            title: `Excluir tela "${t.nome}"?`,
            html: `Slug <span class="mono">${t.slug}</span>. Esta ação é irreversível.`,
            showCancelButton: true,
            confirmButtonText: 'Excluir',
            confirmButtonColor: '#c0392b',
        });
        if (!c.isConfirmed) return;
        const r = await RemoveInterna({ solicitante: user?.email, slug: t.slug, origem: 'built' });
        if (r.Error) return Swal.fire({ icon: 'error', title: 'Falha', text: r.Error });
        Swal.fire({ icon: 'success', title: 'Excluída', timer: 900, showConfirmButton: false });
        const ttl2 = await ConsultaConstrutor({});
        setTelas(Array.isArray(ttl2) ? ttl2 : []);
    }

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num ti">TI</span> Construtor de API</div>
                    <h1 className="page-title">Construtor de API</h1>
                    <p className="page-sub">Crie telas dinâmicas (tabela + dashboard opcional) que viram Páginas Internas automaticamente. Fonte: banco SQL ou API REST.</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn" onClick={publicar}><Icon name="check" size={11}/> Salvar tela</button>
                    <button className="btn ghost" onClick={() => { setForm({ ...VAZIO }); setPasso(0); setPreviewRows([]); setPreviewCols([]); }}>
                        <Icon name="plus" size={11}/> Nova tela
                    </button>
                </div>
            </div>

            <div className="builder-shell">
                <div className="builder-form">
                    <div className="tabs">
                        {PASSOS.map((p, i) => (
                            <button key={p.id} className={`tab ${passo === i ? 'active' : ''}`} onClick={() => setPasso(i)}>
                                <span className="mono" style={{ marginRight: 6 }}>0{i+1}</span>{p.label.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    {passo === 0 && (
                        <>
                            <div className="field"><label>Nome da tela</label><input className="input" value={form.nome} onChange={e => up('nome', e.target.value)}/></div>
                            <div className="field"><label>Descrição</label><textarea className="textarea" rows={2} value={form.descricao} onChange={e => up('descricao', e.target.value)}/></div>
                            <div className="field">
                                <label>Mensagem padrão para compartilhar <span className="muted" style={{ fontSize: 11 }}>(opcional)</span></label>
                                <textarea className="textarea" rows={2} value={form.mensagem_padrao || ''} onChange={e => up('mensagem_padrao', e.target.value)} placeholder="Texto usado como template ao clicar em Compartilhar nesta tela. Ex.: Cotações atualizadas — confira."/>
                            </div>
                            <div className="field"><label>Slug (rota interna)</label><input className="input mono" value={form.slug} onChange={e => up('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))}/></div>
                            <div className="field">
                                <label>Modo de acesso</label>
                                <select className="select" value={form.modo_acesso} onChange={e => up('modo_acesso', e.target.value)}>
                                    <option value="todos">Acesso a todos</option>
                                    <option value="setor">Restrito por setor</option>
                                    <option value="usuarios">Restrito por usuários</option>
                                </select>
                            </div>
                            {form.modo_acesso === 'setor' && (
                                <div className="field">
                                    <label>Setores liberados ({(form.setores || []).length})</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {setoresDisp.map(s => {
                                            const on = (form.setores || []).includes(s);
                                            return (
                                                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--line)', padding: '6px 12px', cursor: 'pointer', background: on ? 'var(--ink)' : 'var(--paper)', color: on ? 'var(--paper)' : 'var(--ink)' }}>
                                                    <input type="checkbox" checked={on} onChange={() => setForm(f => ({ ...f, setores: (f.setores || []).includes(s) ? (f.setores || []).filter(x => x !== s) : [...(f.setores || []), s] }))} style={{ display: 'none' }} />
                                                    {s}
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>TI e Administrador sempre têm acesso.</div>
                                </div>
                            )}
                            {form.modo_acesso === 'usuarios' && (
                                <div className="field">
                                    <label>Usuários liberados ({(form.usuarios || []).length})</label>
                                    <button type="button" className="btn ghost sm" onClick={() => setPopupUsers(true)} style={{ alignSelf: 'flex-start' }}>
                                        <Icon name="shield" size={11}/> Selecionar usuários
                                    </button>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                        {(form.usuarios || []).map(uid => {
                                            const u = usuariosAll.find(x => x.id === uid);
                                            return u && <span className="chip" key={uid}>{u.nome}</span>;
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {passo === 1 && (
                        <>
                            <div className="field">
                                <label>Tipo de origem</label>
                                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                                    <label className="row-flex"><input type="radio" checked={form.origem === 'banco'} onChange={() => up('origem', 'banco')}/> Banco (SQL)</label>
                                    <label className="row-flex"><input type="radio" checked={form.origem === 'rest'} onChange={() => up('origem', 'rest')}/> API REST</label>
                                </div>
                            </div>

                            {form.origem === 'banco' && (
                                <>
                                    <div className="field">
                                        <label>Conexão</label>
                                        <select className="select" value={form.conexao} onChange={e => up('conexao', e.target.value)}>
                                            <option value="">— escolha —</option>
                                            {conexoes.map(c => <option key={c.id} value={c.prefixo}>{c.prefixo} ({c.driver})</option>)}
                                        </select>
                                        {conexoes.length === 0 && <span className="muted" style={{ fontSize: 11 }}>Cadastre uma em TI · Conexões de Banco primeiro.</span>}
                                    </div>
                                    <div className="field">
                                        <label>{'SQL ($1,$2 = args - {{data_ini}}/{{data_fim}} = filtro de periodo do dashboard)'}</label>
                                        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                                            <button type="button" className="btn ghost sm" onClick={() => insereTokenTextarea('cons-sql', form.sql, '{{data_ini}}', v => up('sql', v))} title="Insere o token de data inicial no cursor">{'+ {{data_ini}}'}</button>
                                            <button type="button" className="btn ghost sm" onClick={() => insereTokenTextarea('cons-sql', form.sql, '{{data_fim}}', v => up('sql', v))} title="Insere o token de data final no cursor">{'+ {{data_fim}}'}</button>
                                            <button type="button" className="btn ghost sm" onClick={() => insereTokenTextarea('cons-sql', form.sql, 'BETWEEN {{data_ini}} AND {{data_fim}}', v => up('sql', v))} title="Insere o intervalo completo no cursor">+ BETWEEN periodo</button>
                                        </div>
                                        <textarea id="cons-sql" className="textarea mono" rows={8} value={form.sql} onChange={e => up('sql', e.target.value)}/>
                                    </div>
                                </>
                            )}

                            {form.origem === 'rest' && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10 }}>
                                        <div className="field"><label>Método</label><select className="select" value={form.rest.method} onChange={e => upRest('method', e.target.value)}>
                                            {['GET','POST','PUT','DELETE'].map(m => <option key={m}>{m}</option>)}
                                        </select></div>
                                        <div className="field"><label>URL</label><input className="input mono" placeholder="https://api.exemplo.com/dados" value={form.rest.url} onChange={e => upRest('url', e.target.value)}/></div>
                                    </div>

                                    <div className="field">
                                        <label>Headers (padrão: Content-Type + Accept JSON)</label>
                                        {(form.rest.headers || []).map((h, i) => (
                                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6, marginTop: 6 }}>
                                                <input className="input mono" placeholder="Nome" value={h.nome} onChange={e => updHeader(i, 'nome', e.target.value)}/>
                                                <input className="input mono" placeholder="Valor" value={h.valor} onChange={e => updHeader(i, 'valor', e.target.value)}/>
                                                <button className="btn ghost sm" onClick={() => delHeader(i)}><Icon name="x" size={11}/></button>
                                            </div>
                                        ))}
                                        <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={addHeader}><Icon name="plus" size={11}/> Adicionar header</button>
                                    </div>

                                    <div className="field">
                                        <label>Autenticação</label>
                                        <select className="select" value={form.rest.auth.tipo} onChange={e => upAuth('tipo', e.target.value)}>
                                            <option value="none">Sem autenticação</option>
                                            <option value="bearer">Bearer token</option>
                                            <option value="basic">Basic auth</option>
                                        </select>
                                    </div>
                                    {form.rest.auth.tipo === 'bearer' && (
                                        <div className="field"><label>Token</label><input className="input mono" type="password" value={form.rest.auth.token} onChange={e => upAuth('token', e.target.value)}/></div>
                                    )}
                                    {form.rest.auth.tipo === 'basic' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                            <div className="field"><label>Usuário</label><input className="input" value={form.rest.auth.user} onChange={e => upAuth('user', e.target.value)}/></div>
                                            <div className="field"><label>Senha</label><input className="input" type="password" value={form.rest.auth.senha} onChange={e => upAuth('senha', e.target.value)}/></div>
                                        </div>
                                    )}
                                    <div className="cfg-row-2">
                                        <div className="field">
                                            <label>Formato da resposta</label>
                                            <select className="select" value={form.rest.shape || 'auto'} onChange={e => upRest('shape', e.target.value)}>
                                                <option value="auto">Auto-detectar (recomendado)</option>
                                                <option value="array_obj">{"Array de objetos — [{...},{...}]"}</option>
                                                <option value="array_arr">{"Array de arrays — [[a,b,c],[d,e,f]]"}</option>
                                                <option value="objeto">{"Objeto único — {a:1, b:2}"}</option>
                                                <option value="obj_values">{"Objeto com chaves — {USD:{...}, EUR:{...}} → vira array"}</option>
                                            </select>
                                        </div>
                                        <div className="field">
                                            <label>Path do array (opcional)</label>
                                            <input className="input mono" placeholder="ex: data.items ou results — vazio = auto" value={form.rest.path_rows} onChange={e => upRest('path_rows', e.target.value)}/>
                                        </div>
                                    </div>
                                    <div className="muted" style={{ fontSize: 11, marginTop: -4 }}>
                                        Use o <b>path</b> quando a resposta tem o array dentro de outro campo (ex: <code>{'{data:{rows:[...]}}'}</code> → digite <code>data.rows</code>). Para chaves dinâmicas (ex: <code>{'{USD:{}, EUR:{}}'}</code>), escolha <b>Objeto com chaves</b>.
                                    </div>
                                </>
                            )}

                            <button className="btn sm" onClick={testarOrigem} disabled={testing} style={{ marginTop: 12 }}>
                                <Icon name="play" size={11}/> {testing ? 'Testando…' : 'Testar origem'}
                            </button>
                        </>
                    )}

                    {passo === 2 && (
                        <>
                            <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
                                Defina labels personalizados, ordem e visibilidade. Use "Testar origem" no passo Origem pra autopopular esta lista a partir da query/resposta.
                            </p>
                            {(form.colunas || []).length === 0 && (
                                <div className="empty" style={{ marginBottom: 14 }}>Nenhuma coluna ainda — teste a origem ou adicione manualmente.</div>
                            )}
                            {(form.colunas || []).map((c, i) => (
                                <div key={i} style={{ marginTop: 6, padding: c.tipo === 'acao' ? '8px 10px' : 0, background: c.tipo === 'acao' ? 'var(--bg-2)' : 'transparent', border: c.tipo === 'acao' ? '1px solid var(--line)' : 'none' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 80px auto', gap: 6, alignItems: 'center' }}>
                                        <input className="input mono" placeholder={c.tipo === 'acao' ? 'id (interno)' : 'nome (api)'} value={c.nome} onChange={e => updColuna(i, 'nome', e.target.value)}/>
                                        <input className="input" placeholder={c.tipo === 'acao' ? 'Texto do botão' : 'Label visível'} value={c.label} onChange={e => updColuna(i, 'label', e.target.value)}/>
                                        <select className="select" value={c.tipo} onChange={e => updColuna(i, 'tipo', e.target.value)}>
                                            {['texto','numero','data','boolean','moeda','acao'].map(t => <option key={t}>{t}</option>)}
                                        </select>
                                        <label className="row-flex" style={{ fontSize: 11 }}><input type="checkbox" checked={c.visivel !== false} onChange={e => updColuna(i, 'visivel', e.target.checked)}/> visível</label>
                                        <button className="btn ghost sm" onClick={() => delColuna(i)}><Icon name="trash" size={11}/></button>
                                    </div>
                                    {c.tipo === 'acao' && (
                                        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, alignItems: 'center', fontSize: 11.5 }}>
                                            <label>Método HTTP</label>
                                            <select className="select" value={(c.acao && c.acao.method) || 'GET'} onChange={e => updColuna(i, 'acao', { ...(c.acao || {}), method: e.target.value })}>
                                                {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m}>{m}</option>)}
                                            </select>
                                            <label>URL</label>
                                            <input className="input mono" placeholder="https://api/x/{id}/cancelar  — use {coluna} para interpolar" value={(c.acao && c.acao.url) || ''} onChange={e => updColuna(i, 'acao', { ...(c.acao || {}), url: e.target.value })}/>
                                            <label>Body (opcional)</label>
                                            <textarea className="input mono" rows={3} placeholder='{"motivo":"...","id":{id}}  — usa {coluna} também' value={(c.acao && c.acao.body) || ''} onChange={e => updColuna(i, 'acao', { ...(c.acao || {}), body: e.target.value })}/>
                                            <label>Confirmar?</label>
                                            <label style={{ fontSize: 11.5 }}><input type="checkbox" checked={(c.acao && c.acao.confirmar) !== false} onChange={e => updColuna(i, 'acao', { ...(c.acao || {}), confirmar: e.target.checked })}/> Pedir confirmação antes de executar</label>
                                            <div style={{ gridColumn: '1 / -1', color: 'var(--ink-mute)', fontSize: 10.5 }}>
                                                Substitua <code>{'{nome_da_coluna}'}</code> na URL ou body pelos valores da linha. Ex: <code>https://api/pedidos/{'{id}'}/cancelar</code>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={addColuna}><Icon name="plus" size={11}/> Adicionar coluna</button>
                        </>
                    )}

                    {passo === 3 && (
                        <>
                            <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>Gráficos exibidos acima da tabela na tela final.</p>
                            {(form.charts || []).map((c, i) => (
                                <div key={i} style={{ background: 'var(--bg-2)', padding: 12, marginBottom: 8, border: '1px solid var(--line)' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px auto', gap: 6 }}>
                                        <input className="input" placeholder="Título" value={c.titulo} onChange={e => updChart(i, 'titulo', e.target.value)}/>
                                        <select className="select" value={c.tipo} onChange={e => updChart(i, 'tipo', e.target.value)}>
                                            {CHART_TIPOS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                        </select>
                                        <button className="btn ghost sm" onClick={() => delChart(i)}><Icon name="trash" size={11}/></button>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                                        {camposDoTipo(c.tipo).map(campo => (
                                            <select key={campo} className="select" value={c[campo] || ''} onChange={e => updChart(i, campo, e.target.value)}>
                                                <option value="">{labelCampo(c.tipo, campo)}</option>
                                                {(form.colunas || []).map(col => <option key={col.nome} value={col.nome}>{col.label || col.nome}</option>)}
                                            </select>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={addChart}><Icon name="plus" size={11}/> Adicionar gráfico</button>

                            <div className="divider"/>
                            <button className="btn" onClick={publicar}>
                                <Icon name="check" size={11}/> Publicar tela
                            </button>
                        </>
                    )}
                </div>

                <div className="builder-preview">
                    <div className="eyebrow"><span className="num">04</span> Preview ao vivo</div>
                    <h3 className="preview-title">{form.nome || 'Nome da tela'}</h3>
                    <p className="muted" style={{ fontSize: 13 }}>{form.descricao || 'A descrição aparece aqui.'}</p>

                    {previewRows.length > 0 ? (
                        <table className="preview-table">
                            <thead><tr>{previewCols.filter(c => (form.colunas || []).find(fc => fc.nome === c.nome)?.visivel !== false).map(c => <th key={c.nome}>{(form.colunas || []).find(fc => fc.nome === c.nome)?.label || c.label || c.nome}</th>)}</tr></thead>
                            <tbody>
                                {previewRows.slice(0, 8).map((r, i) => (
                                    <tr key={i}>
                                        {previewCols.filter(c => (form.colunas || []).find(fc => fc.nome === c.nome)?.visivel !== false).map(c => (
                                            <td key={c.nome}>{r[c.nome] != null ? String(r[c.nome]) : ''}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="preview-table">
                            <thead><tr><th>ID</th><th>Nome</th></tr></thead>
                            <tbody>
                                <tr><td className="mono">7</td><td>Valor 1</td></tr>
                                <tr><td className="mono">14</td><td>Valor 2</td></tr>
                            </tbody>
                        </table>
                    )}

                    <div className="eyebrow" style={{ marginTop: 18 }}>Rota gerada</div>
                    <pre className="code-block mono">{`POST /ExecutaTelaInterna
{ slug: "${form.slug || 'minhaTela'}", args: { ... } }

# Tela renderizada em:
/internas/${form.slug || 'minhaTela'}`}</pre>
                </div>
            </div>

            <div className="divider"/>

            <div className="eyebrow"><span className="num">·</span> Telas publicadas</div>
            <div className="card flat" style={{ padding: 0, marginTop: 12 }}>
                <div className="list-rows">
                    {telas.map(t => (
                        <div className="row" key={t.slug} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 110px 110px 44px', alignItems: 'center', gap: 14 }}>
                            <div style={{ minWidth: 0 }}>
                                <b>{t.nome}</b> <span className="mono muted" style={{ fontSize: 11 }}>/internas/{t.slug}</span>
                            </div>
                            <span className="chip" style={{ justifySelf: 'center' }}>{t.modo_acesso || '—'}</span>
                            <span className="chip" style={{ justifySelf: 'center' }}>{labelOrigem(t)}</span>
                            <button className="btn ghost sm" style={{ justifySelf: 'stretch' }} onClick={() => { setForm(normalizaTela(t)); setPasso(0); setPreviewRows([]); setPreviewCols([]); }}>
                                <Icon name="edit" size={11}/> Editar
                            </button>
                            <button className="btn ghost sm" style={{ justifySelf: 'stretch', color: '#c0392b' }} onClick={() => excluirTela(t)} title="Excluir esta tela">
                                <Icon name="trash" size={11}/>
                            </button>
                        </div>
                    ))}
                    {telas.length === 0 && <div className="row muted">Nenhuma tela publicada ainda.</div>}
                </div>
            </div>

            {popupUsers && (
                <UsersPopup
                    todos={usuariosAll}
                    selecionados={form.usuarios || []}
                    onConfirm={(ids) => { up('usuarios', ids); setPopupUsers(false); }}
                    onClose={() => setPopupUsers(false)}
                />
            )}
        </div>
    );
}

function UsersPopup({ todos, selecionados, onConfirm, onClose }) {
    const [sel, setSel] = useState(new Set(selecionados));
    const [busca, setBusca] = useState('');

    function toggle(id) {
        const n = new Set(sel);
        if (n.has(id)) n.delete(id); else n.add(id);
        setSel(n);
    }

    const lista = todos.filter(u => !busca || (u.nome || '').toLowerCase().includes(busca.toLowerCase()) || (u.email || '').toLowerCase().includes(busca.toLowerCase()));

    return (
        <div className="modal-mask" onClick={onClose}>
            <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">Selecionar usuários ({sel.size})</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <input className="input" placeholder="Buscar nome ou e-mail…" value={busca} onChange={e => setBusca(e.target.value)} style={{ marginBottom: 12 }}/>
                    <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid var(--line)' }}>
                        {lista.map(u => (
                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)}/>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13 }}>{u.nome}</div>
                                    <div className="muted mono" style={{ fontSize: 11 }}>{u.email} · {u.setor}</div>
                                </div>
                            </label>
                        ))}
                        {lista.length === 0 && <div className="muted" style={{ padding: 14 }}>Nenhum usuário encontrado.</div>}
                    </div>
                </div>
                <div className="modal-foot">
                    <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
                    <button className="btn sm" onClick={() => onConfirm([...sel])}>Confirmar seleção</button>
                </div>
            </div>
        </div>
    );
}
